//EBITDARoleLegend.tsx
import React from 'react';
import { HelpCircle, Info } from 'lucide-react';

/**
 * Component for displaying the EBITDA Role Legend and Documentation
 */
export const EBITDARoleLegend: React.FC = () => {
  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Interest and Tax */}
        <div className="space-y-3 border-l-4 border-purple-400 pl-4">
          <h4 className="font-medium text-gray-900">Interest and Tax</h4>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Interest Expense
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
              Income Tax Expense
            </span>
          </div>
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              <strong>Core financial components of EBITDA.</strong> These categories are added back to determine EBITDA.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-500">
              <li><strong>Interest Expense</strong>: Added back to net income to neutralize capital structure impact.</li>
              <li><strong>Income Tax Expense</strong>: Added back to normalize for differences in tax jurisdictions.</li>
            </ul>
          </div>
        </div>
        
        {/* Depreciation and Amortization */}
        <div className="space-y-3 border-l-4 border-indigo-400 pl-4">
          <h4 className="font-medium text-gray-900">Depreciation & Amortization</h4>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              Depreciation
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
              Amortization
            </span>
          </div>
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              <strong>Non-cash expenses.</strong> These categories represent the non-cash expenses that are added back in EBITDA calculations.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-500">
              <li><strong>Depreciation</strong>: Non-cash expense related to tangible assets (PP&E).</li>
              <li><strong>Amortization</strong>: Non-cash expense related to intangible assets.</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-blue-50 mt-4 p-3 rounded text-sm text-blue-800 flex items-start">
        <HelpCircle className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
        <div>
          <strong>How it works:</strong> In the Categories tab, assign an EBITDA role to the appropriate financial accounts.
          This ensures that your EBITDA calculations will remain accurate, even if you rename categories or create 
          custom templates. The system will automatically identify the correct components to add back to Net Income.
        </div>
      </div>
    </div>
  );
};

/**
 * Tooltip component to explain EBITDA role
 */
export const EBITDARoleTooltip: React.FC<{ role: string }> = ({ role }) => {
  const getRoleDescription = () => {
    switch (role) {
      case 'interest-expense':
        return 'Interest expense is added back to net income in EBITDA calculations';
      case 'income-tax-expense':
        return 'Income tax expense is added back to net income in EBITDA calculations';
      case 'depreciation':
        return 'Depreciation is a non-cash expense related to tangible assets that is added back in EBITDA calculations';
      case 'amortization':
        return 'Amortization is a non-cash expense related to intangible assets that is added back in EBITDA calculations';
      default:
        return 'Assign an EBITDA role to categorize this item for EBITDA calculations';
    }
  };
  
  return (
    <div className="group relative">
      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
      <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 right-0 bottom-full mb-1 w-52">
        {getRoleDescription()}
      </div>
    </div>
  );
};
